import React, { useCallback, useEffect, useState } from 'react';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import Sidebar from '@/components/Sidebar';
import CalculationBreakdown from '@/components/CalculationBreakdown';
import ImpositionSelector from '@/components/ImpositionSelector';
import api from '@/lib/api';
import {
  applyImpositionDerivedValues,
  getImpositionDerivedValues,
  getImpositionOutputKeys,
  normalizeImpositionConfig,
} from '@/lib/impositionVariables';
import '@/pages/Products.css';
import '@/pages/Clients.css';
import '@/components/SheetImposition.css';

const formatMoney = (n) => `$${(n || 0).toLocaleString('es-AR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
const PRODUCT_CALCULATOR_REVISTA = 'revista';
const PRODUCT_CALCULATOR_CUADERNO = 'cuaderno';
const PRODUCT_CALCULATOR_FACTURAS = 'facturas';
const INVOICE_COPY_ORDER = [
  { label: 'Original', suffix: 'original' },
  { label: 'Duplicado', suffix: 'duplicado' },
  { label: 'Triplicado', suffix: 'triplicado' },
  { label: 'Cuatriplicado', suffix: 'cuatriplicado' },
];

const formatSelectOptionLabel = (variable, option) => {
  const unit = (variable?.unit || '').trim().toLowerCase();
  const value = Number(option?.value);
  if (!Number.isFinite(value)) return option?.label || '';
  if (unit === '$') return `${option.label} (${formatMoney(value)})`;
  if (unit && !['copia', 'copias'].includes(unit)) {
    return `${option.label} (${value.toLocaleString('es-AR')} ${variable.unit})`;
  }
  return option.label;
};

const getInvoiceCopies = (productSnapshot) => {
  const config = productSnapshot?.calculator_config || {};
  const configuredCopies = Array.isArray(config.copies) ? config.copies : [];
  if (configuredCopies.length > 0) return configuredCopies;
  return INVOICE_COPY_ORDER.map(copy => ({
    key: copy.suffix,
    label: copy.label,
    paper_variable: `papel${copy.suffix}`,
    grammage_variable: `gramaje${copy.suffix}`,
    colors_variable: `colores${copy.suffix}`,
    plates_variable: `chapas${copy.suffix}`,
    active_variable: `activo${copy.suffix}`,
  }));
};

const getInvoiceCopyCount = (productSnapshot, values, variables = []) => {
  if (productSnapshot?.calculator_type !== PRODUCT_CALCULATOR_FACTURAS) return INVOICE_COPY_ORDER.length;
  const config = productSnapshot?.calculator_config || {};
  const key = config.copy_count_variable || 'tipofactura';
  const variable = variables.find(v => v.key === key);
  const rawValue = values?.[key] ?? variable?.options?.[0]?.label ?? variable?.default_value ?? 1;
  const numericValue = Number(rawValue);
  if (Number.isFinite(numericValue) && numericValue > 0) {
    return Math.max(1, Math.min(INVOICE_COPY_ORDER.length, Math.round(numericValue)));
  }
  const normalized = String(rawValue || '').trim().toLowerCase();
  const matchedOption = (variable?.options || []).find(opt => (
    String(opt.label || '').trim().toLowerCase() === normalized
  ));
  if (matchedOption) {
    return getInvoiceCopyCount(productSnapshot, { [key]: matchedOption.value }, variables);
  }
  const matchedIndex = INVOICE_COPY_ORDER.findIndex(copy => copy.label.toLowerCase() === normalized);
  return matchedIndex >= 0 ? matchedIndex + 1 : 1;
};

const getActiveInvoiceCopies = (productSnapshot, values, variables = []) => (
  getInvoiceCopies(productSnapshot)
    .slice(0, getInvoiceCopyCount(productSnapshot, values, variables))
    .map((copy, index) => ({
      ...copy,
      key: copy.key || INVOICE_COPY_ORDER[index]?.suffix || `copia-${index + 1}`,
      label: copy.label || INVOICE_COPY_ORDER[index]?.label || `Copia ${index + 1}`,
      index,
    }))
);

const getInactiveInvoiceKeys = (productSnapshot, values, variables = []) => {
  if (productSnapshot?.calculator_type !== PRODUCT_CALCULATOR_FACTURAS) return new Set();
  const count = getInvoiceCopyCount(productSnapshot, values, variables);
  const keys = new Set();
  getInvoiceCopies(productSnapshot).slice(count).forEach((copy, offset) => {
    const fallback = INVOICE_COPY_ORDER[count + offset] || {};
    [
      copy.paper_variable,
      copy.grammage_variable,
      copy.colors_variable,
      copy.plates_variable,
      copy.active_variable,
      fallback.suffix ? `papel${fallback.suffix}` : null,
      fallback.suffix ? `gramaje${fallback.suffix}` : null,
      fallback.suffix ? `colores${fallback.suffix}` : null,
      fallback.suffix ? `chapas${fallback.suffix}` : null,
      fallback.suffix ? `activo${fallback.suffix}` : null,
    ].filter(Boolean).forEach(key => keys.add(key));
  });
  return keys;
};

const applyInvoiceBudgetValues = (productSnapshot, values, variables = []) => {
  if (productSnapshot?.calculator_type !== PRODUCT_CALCULATOR_FACTURAS) return values;
  const next = { ...(values || {}) };
  getInactiveInvoiceKeys(productSnapshot, values, variables).forEach(key => {
    if (/^(gramaje|colores|chapas|activo)/i.test(key)) next[key] = 0;
  });
  return next;
};

const applyInvoiceCopyImpositions = (productSnapshot, values, choices = {}, variables = []) => {
  if (productSnapshot?.calculator_type !== PRODUCT_CALCULATOR_FACTURAS) return values;
  let next = { ...(values || {}) };
  const activeKeys = new Set(getActiveInvoiceCopies(productSnapshot, values, variables).map(copy => copy.key));
  const activeDerived = [];
  getInvoiceCopies(productSnapshot).forEach((copy, index) => {
    const normalizedCopy = {
      ...copy,
      key: copy.key || INVOICE_COPY_ORDER[index]?.suffix || `copia-${index + 1}`,
      label: copy.label || INVOICE_COPY_ORDER[index]?.label || `Copia ${index + 1}`,
    };
    const outputKeys = {
      sheets: normalizedCopy.sheets_variable || `pliegos${normalizedCopy.key}`,
      motherSheet: normalizedCopy.mother_sheet_variable || `hojamadre${normalizedCopy.key}`,
      spoilage: normalizedCopy.spoilage_variable || `desperdicio${normalizedCopy.key}`,
      sheetWidth: normalizedCopy.sheet_width_variable || `anchohoja${normalizedCopy.key}`,
      sheetHeight: normalizedCopy.sheet_height_variable || `largohoja${normalizedCopy.key}`,
    };
    const saved = choices[normalizedCopy.key];
    if (!activeKeys.has(normalizedCopy.key) || !saved?.choice || !saved?.config) {
      Object.values(outputKeys).forEach(key => {
        next[key] = Number(next[key]) || 0;
      });
      return;
    }
    const configWithoutSpoilage = {
      ...saved.config,
      spoilageSheets: 0,
      spoilage_sheets: 0,
    };
    const derived = getImpositionDerivedValues(saved.choice, configWithoutSpoilage, {
      sheets_variable: outputKeys.sheets,
      mother_sheet_variable: outputKeys.motherSheet,
      spoilage_variable: outputKeys.spoilage,
      sheet_width_variable: outputKeys.sheetWidth,
      sheet_height_variable: outputKeys.sheetHeight,
    });
    activeDerived.push({
      outputKeys,
      saved,
      derived,
      baseMotherSheets: Math.max(0, Number(derived[outputKeys.motherSheet]) || 0),
      generatedSheetsPerMother: Math.max(1, Number(saved.config?.sourceCut?.sheetCount) || 1),
    });
  });

  const totalSpoilageMotherSheets = activeDerived.length > 0
    ? Math.max(0, Math.ceil(Number(activeDerived[0].saved.config?.spoilageSheets ?? activeDerived[0].saved.config?.spoilage_sheets) || 0))
    : 0;
  const totalBaseMotherSheets = activeDerived.reduce((sum, item) => sum + item.baseMotherSheets, 0);
  let allocatedSpoilage = 0;

  activeDerived.forEach((item, index) => {
    let spoilageForCopy = 0;
    if (totalSpoilageMotherSheets > 0) {
      if (index === activeDerived.length - 1) {
        spoilageForCopy = totalSpoilageMotherSheets - allocatedSpoilage;
      } else if (totalBaseMotherSheets > 0) {
        spoilageForCopy = Math.floor((item.baseMotherSheets / totalBaseMotherSheets) * totalSpoilageMotherSheets);
      }
    }
    allocatedSpoilage += spoilageForCopy;
    next = {
      ...next,
      ...item.derived,
      [item.outputKeys.spoilage]: spoilageForCopy,
      [item.outputKeys.motherSheet]: item.baseMotherSheets + spoilageForCopy,
      [item.outputKeys.sheets]: (Number(item.derived[item.outputKeys.sheets]) || 0) + (spoilageForCopy * item.generatedSheetsPerMother),
    };
  });
  return next;
};

const normalizeMagazineElements = (config = {}) => (
  (config.elements || []).filter(element => element?.page_count_variable)
);

const isPageCalculatorProduct = (productSnapshot) => (
  productSnapshot?.calculator_type === PRODUCT_CALCULATOR_REVISTA
  || productSnapshot?.calculator_type === PRODUCT_CALCULATOR_CUADERNO
);

const DEFAULT_COVER_CONFIG = {
  enabled_variable: 'tienetapa',
  front_colors_variable: 'colortapafrente',
  back_colors_variable: 'colortapadorso',
  plate_mode_variable: 'modochapastapa',
  plates_total_variable: 'chapastapa',
  print_sheets_variable: 'pliegostapa',
  mother_sheet_variable: 'hojamadretapa',
  spoilage_variable: 'desperdiciotapa',
  sheet_width_variable: 'anchohojatapa',
  sheet_height_variable: 'largohojatapa',
  pages_per_sheet_variable: 'paginasporpliegotapa',
};

const normalizeCoverConfig = (config = {}) => ({
  ...DEFAULT_COVER_CONFIG,
  ...(config || {}),
});

const getCoverConfig = (productSnapshot) => normalizeCoverConfig(productSnapshot?.calculator_config?.cover || {});

const hasCoverEnabled = (productSnapshot, values = {}) => {
  const cover = getCoverConfig(productSnapshot);
  return Boolean(isPageCalculatorProduct(productSnapshot) && cover.enabled && Number(values?.[cover.enabled_variable]) > 0);
};

const getCoverInactiveKeys = (productSnapshot, values = {}) => {
  const cover = getCoverConfig(productSnapshot);
  if (!isPageCalculatorProduct(productSnapshot) || !cover.enabled || hasCoverEnabled(productSnapshot, values)) return new Set();
  return new Set([
    cover.front_colors_variable,
    cover.back_colors_variable,
    cover.plate_mode_variable,
    cover.plates_total_variable,
    cover.print_sheets_variable,
    cover.mother_sheet_variable,
    cover.spoilage_variable,
    cover.sheet_width_variable,
    cover.sheet_height_variable,
    cover.pages_per_sheet_variable,
    'largotapa',
    'anchotapa',
    'gramajetapa',
    'papeltapa',
    'preparaciontapa',
    'planchatapa',
    'papelcalctapa',
    'tintacalctapa',
    'impresiontapa',
    'guillotinatapa',
    'empaquetadotapa',
    'costotapa',
  ].filter(Boolean));
};

const applyCoverImposition = (productSnapshot, values, coverImposition) => {
  if (!hasCoverEnabled(productSnapshot, values) || !coverImposition?.choice || !coverImposition?.config) return values;
  const cover = getCoverConfig(productSnapshot);
  const derived = getImpositionDerivedValues(coverImposition.choice, coverImposition.config, {
    sheets_variable: cover.print_sheets_variable,
    mother_sheet_variable: cover.mother_sheet_variable,
    spoilage_variable: cover.spoilage_variable,
    sheet_width_variable: cover.sheet_width_variable,
    sheet_height_variable: cover.sheet_height_variable,
    pages_per_sheet_variable: cover.pages_per_sheet_variable,
  });
  return { ...values, ...derived };
};

const buildMagazineBudgetConfig = (productSnapshot, interiorCount) => {
  const config = productSnapshot?.calculator_config || {};
  const elements = normalizeMagazineElements(config);
  if (!isPageCalculatorProduct(productSnapshot) || elements.length === 0) {
    return productSnapshot?.calculator_config || {};
  }
  const count = productSnapshot?.calculator_type === PRODUCT_CALCULATOR_CUADERNO
    ? 1
    : Math.max(1, Math.min(Number(interiorCount) || 1, elements.length));
  return {
    ...config,
    calculator_type: productSnapshot?.calculator_type,
    cover: normalizeCoverConfig(config.cover || {}),
    elements: elements.slice(0, count).map((element, index) => ({
      ...element,
      pages_per_sheet_variable: element.pages_per_sheet_variable || `paginasporpliegoint${index + 1}`,
      mother_sheet_variable: element.mother_sheet_variable || `hojamadreint${index + 1}`,
      spoilage_variable: element.spoilage_variable || `desperdicioint${index + 1}`,
      sheet_width_variable: element.sheet_width_variable || `anchohojaint${index + 1}`,
      sheet_height_variable: element.sheet_height_variable || `largohojaint${index + 1}`,
    })),
  };
};

const getMagazineElementKey = (element, index) => element.page_count_variable || `interior-${index + 1}`;

const getInactiveMagazineKeys = (productSnapshot, interiorCount) => {
  const config = productSnapshot?.calculator_config || {};
  const elements = normalizeMagazineElements(config);
  if (!isPageCalculatorProduct(productSnapshot)) return new Set();
  const count = Math.max(1, Math.min(Number(interiorCount) || 1, elements.length || 1));
  const keys = new Set();
  elements.slice(count).forEach((element, offset) => {
    const index = count + offset + 1;
    [
      element.page_count_variable,
      element.front_colors_variable,
      element.back_colors_variable,
      element.pages_per_sheet_variable,
      element.mother_sheet_variable,
      element.spoilage_variable,
      element.sheet_width_variable,
      element.sheet_height_variable,
      element.distinct_sheets_variable,
      element.faces_variable,
      element.plates_variable,
      element.print_sheets_variable,
      `paginasint${index}`,
      `gramajeint${index}`,
      `papelint${index}`,
      `colorint${index}frente`,
      `colorint${index}dorso`,
      `papelint${index}calc`,
      `tintaint${index}`,
      `paginasporpliegoint${index}`,
      `hojamadreint${index}`,
      `desperdicioint${index}`,
      `anchohojaint${index}`,
      `largohojaint${index}`,
      `pliegosint${index}`,
      `carasint${index}`,
      `chapasint${index}`,
      `tiradaint${index}`,
    ].filter(Boolean).forEach(key => keys.add(key));
  });
  return keys;
};

const applyMagazineBudgetValues = (productSnapshot, interiorCount, values) => {
  const next = { ...(values || {}) };
  getInactiveMagazineKeys(productSnapshot, interiorCount).forEach(key => {
    next[key] = /^paginasporpliegoint\d+$/.test(key) ? 1 : 0;
  });
  return next;
};

const applyMagazinePagesPerSheet = (productSnapshot, values, impositionChoice, impositionConfig) => {
  if (!isPageCalculatorProduct(productSnapshot) || !impositionChoice) return values;
  const key = productSnapshot.calculator_config?.pages_per_sheet_variable || 'paginasporpliego';
  if (!key) return values;
  const frontItems = Number(impositionChoice.items_per_sheet) || 0;
  if (frontItems <= 0) return values;
  const faces = impositionConfig?.doubleSided ? 2 : 1;
  return {
    ...values,
    [key]: frontItems * faces,
  };
};

const applyMagazineInteriorImpositions = (calculatorConfig, values, choices = {}) => {
  let next = { ...(values || {}) };
  const isNotebook = calculatorConfig?.calculator_type === PRODUCT_CALCULATOR_CUADERNO;
  (calculatorConfig?.elements || []).forEach((element, index) => {
    const key = getMagazineElementKey(element, index);
    const saved = choices[key];
    if (!saved?.choice || !saved?.config) return;
    const derived = getImpositionDerivedValues(saved.choice, saved.config, {
      sheets_variable: element.print_sheets_variable || `tiradaint${index + 1}`,
      mother_sheet_variable: element.mother_sheet_variable || `hojamadreint${index + 1}`,
      spoilage_variable: element.spoilage_variable || `desperdicioint${index + 1}`,
      sheet_width_variable: element.sheet_width_variable || `anchohojaint${index + 1}`,
      sheet_height_variable: element.sheet_height_variable || `largohojaint${index + 1}`,
      pages_per_sheet_variable: element.pages_per_sheet_variable || `paginasporpliegoint${index + 1}`,
    });
    const faceMultiplier = !isNotebook && saved.config?.doubleSided ? 2 : 1;
    const pagesPerSheetKey = element.pages_per_sheet_variable || `paginasporpliegoint${index + 1}`;
    const printSheetsKey = element.print_sheets_variable || `tiradaint${index + 1}`;
    const motherSheetsKey = element.mother_sheet_variable || `hojamadreint${index + 1}`;
    const spoilageKey = element.spoilage_variable || `desperdicioint${index + 1}`;
    if (saved.choice.items_per_sheet) {
      const pagesPerSheet = Number(saved.choice.items_per_sheet) * faceMultiplier;
      derived[pagesPerSheetKey] = pagesPerSheet;
      const quantityKey = calculatorConfig.quantity_variable || 'cantidad';
      const quantity = Number(next[quantityKey]) || 0;
      const pages = Number(next[element.page_count_variable]) || 0;
      const generatedSheetsPerMother = Math.max(Number(saved.config?.sourceCut?.sheetCount) || 1, 1);
      const spoilageMotherSheets = Math.max(
        0,
        Math.ceil(Number(saved.config?.spoilageSheets ?? saved.config?.spoilage_sheets) || 0)
      );
      if (quantity > 0 && pages > 0 && pagesPerSheet > 0) {
        const basePrintSheets = Math.ceil((quantity * pages) / pagesPerSheet);
        derived[printSheetsKey] = basePrintSheets + (spoilageMotherSheets * generatedSheetsPerMother);
        derived[motherSheetsKey] = Math.ceil(basePrintSheets / generatedSheetsPerMother) + spoilageMotherSheets;
        derived[spoilageKey] = spoilageMotherSheets;
      }
    }
    next = { ...next, ...derived };
  });
  return next;
};

const getImpositionDefaultConfig = (budget) => {
  const saved = budget?.imposition_choice?.config;
  if (saved) return saved;

  const cfg = budget?.product_snapshot?.imposition_config;
  if (!cfg) return {};
  const normalized = normalizeImpositionConfig(cfg);

  return {
    sheetWidth: normalized.sheet_width,
    sheetHeight: normalized.sheet_height,
    gripperTop: normalized.gripper_top,
    gripperBottom: normalized.gripper_bottom,
    marginLeft: normalized.margin_left,
    marginRight: normalized.margin_right,
    bleed: normalized.bleed,
    separation: normalized.separation,
    spoilageSheets: normalized.spoilage_sheets,
    allowRotation: normalized.allow_rotation,
    doubleSided: normalized.double_sided,
  };
};

const getBudgetVariables = (budget) => [
  ...(budget?.product_snapshot?.global_variables || []).map(v => ({ ...v, global: true })),
  ...(budget?.product_snapshot?.variables || []),
];

const BudgetEditor = () => {
  const { id } = useParams();
  const location = useLocation();
  const navigate = useNavigate();

  const [budget, setBudget] = useState(null);
  const [values, setValues] = useState({});
  const [profitPercentage, setProfitPercentage] = useState(0);
  const [additionalCost, setAdditionalCost] = useState('');
  const [observations, setObservations] = useState('');
  const [magazineInteriorCount, setMagazineInteriorCount] = useState(1);
  const [impositionChoice, setImpositionChoice] = useState(null);
  const [impositionConfig, setImpositionConfig] = useState(null);
  const [magazineImpositions, setMagazineImpositions] = useState({});
  const [invoiceImpositions, setInvoiceImpositions] = useState({});
  const [coverImposition, setCoverImposition] = useState(null);
  const [preview, setPreview] = useState(null);
  const [previewError, setPreviewError] = useState('');
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState('');

  useEffect(() => {
    const load = async () => {
      try {
        setLoading(true);
        const { data } = await api.get(`/budgets/${id}`);
        setBudget(data);
        setValues(data.variable_values || {});
        const savedOverrideElements = normalizeMagazineElements(data.calculator_config_override || {});
        const productElements = normalizeMagazineElements(data.product_snapshot?.calculator_config || {});
        setMagazineInteriorCount(
          isPageCalculatorProduct(data.product_snapshot)
            ? Math.max(1, savedOverrideElements.length || Math.min(productElements.length || 1, 1))
            : 1
        );
        setProfitPercentage(data.profit_percentage ?? data.product_snapshot?.profit_percentage ?? 0);
        setAdditionalCost(data.additional_cost ? String(data.additional_cost) : '');
        setObservations(data.observations || '');
        setImpositionChoice(data.imposition_choice || null);
        setImpositionConfig(data.imposition_choice?.config || null);
        setMagazineImpositions(data.magazine_imposition_choices || {});
        setCoverImposition(data.cover_imposition_choice || null);
        setInvoiceImpositions(data.invoice_imposition_choices || {});
      } catch (err) {
        setSaveError('Presupuesto no encontrado');
      } finally {
        setLoading(false);
      }
    };

    load();
  }, [id]);

  const calculatePreview = useCallback(async () => {
    if (!budget?.product_snapshot) return;
    setPreviewError('');
    try {
      const variables = getBudgetVariables(budget);
      const selectedCalculatorConfig = buildMagazineBudgetConfig(budget.product_snapshot, magazineInteriorCount);
      const valuesWithMagazineChoice = applyInvoiceBudgetValues(
        budget.product_snapshot,
        applyMagazineBudgetValues(budget.product_snapshot, magazineInteriorCount, values),
        variables
      );
      const baseCalculationValues = budget.product_snapshot?.imposition_config
        ? applyMagazinePagesPerSheet(
          budget.product_snapshot,
          applyImpositionDerivedValues(
          valuesWithMagazineChoice,
          impositionChoice,
          impositionConfig,
          normalizeImpositionConfig(budget.product_snapshot.imposition_config, variables)
          ),
          impositionChoice,
          impositionConfig
        )
        : valuesWithMagazineChoice;
      const calculationValues = isPageCalculatorProduct(budget.product_snapshot)
        ? applyCoverImposition(
          budget.product_snapshot,
          applyMagazineInteriorImpositions(selectedCalculatorConfig, baseCalculationValues, magazineImpositions),
          coverImposition
        )
        : applyInvoiceCopyImpositions(budget.product_snapshot, baseCalculationValues, invoiceImpositions, variables);
      const { data } = await api.post('/products/preview-formula', {
        variables,
        formula: budget.product_snapshot.formula,
        profit_percentage: profitPercentage,
        additional_cost: Number(additionalCost) || 0,
        values: calculationValues,
        production_type: budget.product_snapshot.production_type,
        calculator_type: budget.product_snapshot.calculator_type,
        calculator_config: selectedCalculatorConfig,
        imposition_config: budget.product_snapshot.imposition_config,
        imposition_choice: impositionChoice ? { choice: impositionChoice, config: impositionConfig } : null,
        magazine_imposition_choices: magazineImpositions,
        cover_imposition_choice: coverImposition,
        invoice_imposition_choices: invoiceImpositions,
        production_time_config: budget.product_snapshot.production_time_config,
      });
      setPreview(data);
    } catch (err) {
      const detail = err.response?.data?.detail;
      setPreviewError(typeof detail === 'string' ? detail : 'Error al calcular');
      setPreview(null);
    }
  }, [budget, values, profitPercentage, additionalCost, impositionChoice, impositionConfig, magazineInteriorCount, magazineImpositions, coverImposition, invoiceImpositions]);

  useEffect(() => {
    if (!budget) return;
    const timer = setTimeout(() => calculatePreview(), 350);
    return () => clearTimeout(timer);
  }, [budget, values, calculatePreview]);

  useEffect(() => {
    if (!hasCoverEnabled(budget?.product_snapshot, values)) {
      setCoverImposition(null);
    }
  }, [budget, values]);

  const handleSave = async () => {
    setSaveError('');
    setSaving(true);
    try {
      const selectedCalculatorConfig = buildMagazineBudgetConfig(budget?.product_snapshot, magazineInteriorCount);
      const valuesWithMagazineChoice = applyInvoiceBudgetValues(
        budget?.product_snapshot,
        applyMagazineBudgetValues(budget?.product_snapshot, magazineInteriorCount, values),
        getBudgetVariables(budget)
      );
      const baseFinalValues = budget?.product_snapshot?.imposition_config
        ? applyMagazinePagesPerSheet(
          budget.product_snapshot,
          applyImpositionDerivedValues(
          valuesWithMagazineChoice,
          impositionChoice,
          impositionConfig,
          normalizeImpositionConfig(budget.product_snapshot.imposition_config, getBudgetVariables(budget))
          ),
          impositionChoice,
          impositionConfig
        )
        : valuesWithMagazineChoice;
      const finalValues = isPageCalculatorProduct(budget.product_snapshot)
        ? applyCoverImposition(
          budget.product_snapshot,
          applyMagazineInteriorImpositions(selectedCalculatorConfig, baseFinalValues, magazineImpositions),
          coverImposition
        )
        : applyInvoiceCopyImpositions(budget.product_snapshot, baseFinalValues, invoiceImpositions, getBudgetVariables(budget));
      const normalizedConfig = budget?.product_snapshot?.imposition_config
        ? normalizeImpositionConfig(budget.product_snapshot.imposition_config, getBudgetVariables(budget))
        : null;
      const impositionDerivedValues = normalizedConfig
        ? getImpositionDerivedValues(impositionChoice, impositionConfig, normalizedConfig)
        : {};
      const sheetsVariable = normalizedConfig?.sheets_variable;
      const motherSheetVariable = normalizedConfig?.mother_sheet_variable;
      const spoilageVariable = normalizedConfig?.spoilage_variable;
      await api.put(`/budgets/${id}`, {
        variable_values: finalValues,
        profit_percentage: Number(profitPercentage) || 0,
        additional_cost: Number(additionalCost) || 0,
        observations,
        calculator_config_override: isPageCalculatorProduct(budget.product_snapshot)
          ? selectedCalculatorConfig
          : null,
        magazine_imposition_choices: isPageCalculatorProduct(budget.product_snapshot)
          ? magazineImpositions
          : {},
        cover_imposition_choice: isPageCalculatorProduct(budget.product_snapshot)
          ? coverImposition
          : null,
        invoice_imposition_choices: budget.product_snapshot?.calculator_type === PRODUCT_CALCULATOR_FACTURAS
          ? invoiceImpositions
          : {},
        expected_updated_at: budget.updated_at || budget.created_at,
        imposition_choice: impositionChoice ? {
          id: impositionChoice.id,
          label: impositionChoice.label,
          items_per_sheet: impositionChoice.items_per_sheet,
          base_sheets_needed: impositionChoice.sheets_needed,
          sheets_needed: Number(impositionDerivedValues[sheetsVariable]) || impositionChoice.sheets_needed,
          mother_sheets_needed: Number(impositionDerivedValues[motherSheetVariable]) || 0,
          spoilage_sheets: Number(impositionDerivedValues[spoilageVariable]) || 0,
          usage: impositionChoice.usage,
          pieces: impositionChoice.pieces,
          config: impositionConfig,
          sheet_cut: impositionConfig?.sourceCut || null,
        } : null,
      });
      navigate(`/presupuestos/${id}`);
    } catch (err) {
      const detail = err.response?.data?.detail;
      setSaveError(typeof detail === 'string' ? detail : 'Error al guardar cambios');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="dashboard-container">
        <Sidebar activePath={location.pathname} />
        <main className="main-content">
          <div className="empty-state">Cargando...</div>
        </main>
      </div>
    );
  }

  if (!budget) {
    return (
      <div className="dashboard-container">
        <Sidebar activePath={location.pathname} />
        <main className="main-content">
          <div className="login-error">{saveError || 'Presupuesto no encontrado'}</div>
          <button className="btn-secondary" onClick={() => navigate('/presupuestos')} style={{ marginTop: '1rem' }}>
            Volver
          </button>
        </main>
      </div>
    );
  }

  const variables = getBudgetVariables(budget);
  const cfg = budget.product_snapshot?.imposition_config
    ? normalizeImpositionConfig(budget.product_snapshot.imposition_config, variables)
    : null;
  const impositionOutputKeys = new Set(cfg ? getImpositionOutputKeys(cfg) : []);
  const magazineElements = normalizeMagazineElements(budget.product_snapshot?.calculator_config || {});
  const inactiveMagazineKeys = getInactiveMagazineKeys(budget.product_snapshot, magazineInteriorCount);
  const inactiveInvoiceKeys = getInactiveInvoiceKeys(budget.product_snapshot, values, variables);
  const inactiveCoverKeys = getCoverInactiveKeys(budget.product_snapshot, values);
  const inputVariables = variables.filter(v => (
    v.type !== 'calculated'
    && !impositionOutputKeys.has(v.key)
    && !inactiveMagazineKeys.has(v.key)
    && !inactiveInvoiceKeys.has(v.key)
    && !inactiveCoverKeys.has(v.key)
    && v.show_in_budget !== false
  ));
  const itemW = cfg?.width_variable ? Number(values[cfg.width_variable]) || 0 : 0;
  const itemH = cfg?.height_variable ? Number(values[cfg.height_variable]) || 0 : 0;
  const qty = cfg?.quantity_variable ? Number(values[cfg.quantity_variable]) || 1 : 1;
  const canRenderImposition = cfg && itemW > 0 && itemH > 0;
  const activeInvoiceCopies = getActiveInvoiceCopies(budget.product_snapshot, values, variables);

  return (
    <div className="dashboard-container">
      <Sidebar activePath={location.pathname} />

      <main className="main-content">
        <div className="page-header-row">
          <div>
            <h1>Editar Presupuesto <span className="mono">#{String(budget.budget_number).padStart(4, '0')}</span></h1>
            <p className="dashboard-subtitle">
              Se recalcula usando la formula y variables guardadas en este presupuesto.
            </p>
          </div>
          <button className="btn-secondary" onClick={() => navigate(`/presupuestos/${id}`)}>
            Volver
          </button>
        </div>

        <div className="card editor-card">
          <h3 className="card-title">Cliente y Producto</h3>
          <div className="detail-grid">
            <div><strong>Cliente:</strong> {budget.client_snapshot?.name || '-'}</div>
            <div><strong>Producto:</strong> {budget.product_snapshot?.name || '-'}</div>
          </div>
        </div>

        <div className="card editor-card">
          <h3 className="card-title">Valores del Presupuesto</h3>

          {budget.product_snapshot?.calculator_type === PRODUCT_CALCULATOR_REVISTA && magazineElements.length > 0 && (
            <div className="form-group">
              <label className="form-label">Cantidad de interiores</label>
              <select
                className="form-input"
                value={magazineInteriorCount}
                onChange={(e) => setMagazineInteriorCount(Number(e.target.value) || 1)}
              >
                {magazineElements.map((_, index) => (
                  <option key={index + 1} value={index + 1}>
                    {index + 1} interior{index + 1 > 1 ? 'es' : ''}
                  </option>
                ))}
              </select>
              <div className="input-hint">
                Esta eleccion recalcula solamente este presupuesto.
              </div>
            </div>
          )}

          {inputVariables.map(v => (
            <div key={`${v.global ? 'global' : 'product'}-${v.key}`} className="form-group">
              <label className="form-label">
                {v.label || v.key} {v.global && <span className="unit-hint">(global)</span>} {v.unit && <span className="unit-hint">({v.unit})</span>}
              </label>

              {v.type === 'number' && (
                <input
                  type="number"
                  className="form-input"
                  value={values[v.key] ?? ''}
                  onChange={(e) => setValues({ ...values, [v.key]: e.target.value })}
                />
              )}

              {v.type === 'select' && (
                <select
                  className="form-input"
                  value={values[v.key] || ''}
                  onChange={(e) => setValues({ ...values, [v.key]: e.target.value })}
                >
                  {(v.options || []).map((opt, oi) => (
                    <option key={oi} value={opt.label}>
                      {formatSelectOptionLabel(v, opt)}
                    </option>
                  ))}
                </select>
              )}

              {v.type === 'checkbox' && (
                <label className="checkbox-label">
                  <input
                    type="checkbox"
                    checked={Boolean(values[v.key])}
                    onChange={(e) => setValues({ ...values, [v.key]: e.target.checked })}
                  />
                  {' '}Aplicar
                </label>
              )}
            </div>
          ))}

          <div className="form-group">
            <label className="form-label">Porcentaje de ganancia (%)</label>
            <input
              type="number"
              className="form-input"
              value={profitPercentage}
              onChange={(e) => setProfitPercentage(e.target.value)}
              step="0.1"
            />
          </div>

          <div className="form-group">
            <label className="form-label">Costo adicional ($)</label>
            <input
              type="number"
              className="form-input"
              value={additionalCost}
              onChange={(e) => setAdditionalCost(e.target.value)}
              min="0"
              step="0.01"
              placeholder="0"
            />
          </div>

          {previewError && <div className="login-error">{previewError}</div>}

          {preview && (
            <div className="test-result">
              <div className="test-result-row">
                <span>Costo base:</span>
                <span className="mono">{formatMoney(preview.base_cost)}</span>
              </div>
              <div className="test-result-row">
                <span>Ganancia ({preview.profit_percentage}%):</span>
                <span className="mono">{formatMoney(preview.profit_amount ?? 0)}</span>
              </div>
              {Number(preview.additional_cost || 0) > 0 && (
                <div className="test-result-row">
                  <span>Costo adicional:</span>
                  <span className="mono">{formatMoney(preview.additional_cost)}</span>
                </div>
              )}
              <div className="test-result-row test-result-final">
                <span>Precio final:</span>
                <span className="mono">{formatMoney(preview.final_price)}</span>
              </div>
              {preview.production_time && (
                <>
                  <div className="test-result-row">
                    <span>Variable tiempo:</span>
                    <span className="mono">
                      {preview.production_time.total_hours.toLocaleString('es-AR', { maximumFractionDigits: 4 })} h
                    </span>
                  </div>
                  <div className="test-result-row">
                    <span>Tiempo estimado:</span>
                    <span className="mono">
                      {preview.production_time.total_minutes.toLocaleString('es-AR', { maximumFractionDigits: 0 })} min
                    </span>
                  </div>
                  <div className="test-result-row">
                    <span>Variable tiempo_minutos:</span>
                    <span className="mono">
                      {preview.production_time.total_minutes.toLocaleString('es-AR', { maximumFractionDigits: 2 })} min
                    </span>
                  </div>
                </>
              )}
              <CalculationBreakdown
                preview={preview}
                variables={variables}
                formula={budget.product_snapshot?.formula}
              />
            </div>
          )}
        </div>

        {cfg
          && !isPageCalculatorProduct(budget.product_snapshot)
          && budget.product_snapshot?.calculator_type !== PRODUCT_CALCULATOR_FACTURAS && (
          <div className="card editor-card">
            <h3 className="card-title">Imposicion de Pliego</h3>
            {!canRenderImposition ? (
              <div className="empty-state">
                Ingresa ancho y alto de pieza para recalcular la imposicion.
              </div>
            ) : (
              <ImpositionSelector
                value={impositionChoice}
                onChange={(opt, config) => {
                  setImpositionChoice(opt);
                  setImpositionConfig(config);
                  setValues(current => applyMagazinePagesPerSheet(
                    budget.product_snapshot,
                    applyImpositionDerivedValues(current, opt, config, cfg),
                    opt,
                    config
                  ));
                }}
                defaultConfig={getImpositionDefaultConfig(budget)}
                itemWidth={itemW}
                itemHeight={itemH}
                quantity={qty}
              />
            )}
          </div>
        )}

        {cfg && budget.product_snapshot?.calculator_type === PRODUCT_CALCULATOR_FACTURAS && (
          <div className="card editor-card">
            <h3 className="card-title">Imposicion por copia</h3>
            <p className="dashboard-subtitle" style={{ marginBottom: '1rem' }}>
              Cada copia puede tener su propia hoja madre, corte y grafico de imposicion.
            </p>
            {!canRenderImposition ? (
              <div className="empty-state">
                Ingresa ancho y alto de pieza para recalcular la imposicion.
              </div>
            ) : (
              activeInvoiceCopies.map((copy) => {
                const saved = invoiceImpositions[copy.key];
                return (
                  <div key={copy.key} className="card editor-card">
                    <h4 className="card-title">{copy.label}</h4>
                    <ImpositionSelector
                      value={saved?.choice || null}
                      onChange={(opt, config) => {
                        setInvoiceImpositions(current => ({
                          ...current,
                          [copy.key]: opt ? {
                            choice: {
                              id: opt.id,
                              label: opt.label,
                              items_per_sheet: opt.items_per_sheet,
                              base_sheets_needed: opt.sheets_needed,
                              sheets_needed: opt.sheets_needed,
                              usage: opt.usage,
                              pieces: opt.pieces,
                              sheet_cut: config?.sourceCut || null,
                            },
                            config,
                          } : null,
                        }));
                      }}
                      defaultConfig={getImpositionDefaultConfig(budget)}
                      itemWidth={itemW}
                      itemHeight={itemH}
                      quantity={qty}
                    />
                  </div>
                );
              })
            )}
          </div>
        )}

        {cfg && isPageCalculatorProduct(budget.product_snapshot) && (
          <div className="card editor-card">
            <h3 className="card-title">Imposicion por Interior</h3>
            <p className="dashboard-subtitle" style={{ marginBottom: '1rem' }}>
              Cada interior puede tener su propia hoja madre, corte y grafico de imposicion.
            </p>
            {!canRenderImposition ? (
              <div className="empty-state">
                Ingresa ancho y alto de pieza para recalcular la imposicion.
              </div>
            ) : (
              buildMagazineBudgetConfig(budget.product_snapshot, magazineInteriorCount).elements.map((element, index) => {
                const elementKey = getMagazineElementKey(element, index);
                const saved = magazineImpositions[elementKey];
                const elementUnits = Number(values[element.page_count_variable]) || 1;
                const selectorQuantity = budget.product_snapshot?.calculator_type === PRODUCT_CALCULATOR_CUADERNO
                  ? qty * elementUnits
                  : qty;
                return (
                  <div key={elementKey} className="card editor-card">
                    <h4 className="card-title">{element.label || `Interior ${index + 1}`}</h4>
                    <ImpositionSelector
                      value={saved?.choice || null}
                      onChange={(opt, config) => {
                        setMagazineImpositions(current => ({
                          ...current,
                          [elementKey]: opt ? {
                            choice: {
                              id: opt.id,
                              label: opt.label,
                              items_per_sheet: opt.items_per_sheet,
                              base_sheets_needed: opt.sheets_needed,
                              sheets_needed: opt.sheets_needed,
                              usage: opt.usage,
                              pieces: opt.pieces,
                              sheet_cut: config?.sourceCut || null,
                            },
                            config,
                          } : null,
                        }));
                      }}
                      defaultConfig={{
                        ...getImpositionDefaultConfig(budget),
                        doubleSided: true,
                      }}
                      itemWidth={itemW}
                      itemHeight={itemH}
                      quantity={selectorQuantity}
                      doubleSidedCountsAsExtraItems={budget.product_snapshot?.calculator_type !== PRODUCT_CALCULATOR_CUADERNO}
                    />
                  </div>
                );
              })
            )}
          </div>
        )}

        {cfg && hasCoverEnabled(budget.product_snapshot, values) && (() => {
          const cover = getCoverConfig(budget.product_snapshot);
          const coverW = Number(values.anchotapa) || itemW;
          const coverH = Number(values.largotapa) || itemH;
          const canRenderCover = coverW > 0 && coverH > 0;
          return (
            <div className="card editor-card">
              <h3 className="card-title">Imposicion de Tapa</h3>
              <p className="dashboard-subtitle" style={{ marginBottom: '1rem' }}>
                La tapa conserva una imposicion independiente del interior.
              </p>
              {!canRenderCover ? (
                <div className="empty-state">
                  Ingresa ancho y largo de tapa para recalcular la imposicion.
                </div>
              ) : (
                <ImpositionSelector
                  value={coverImposition?.choice || null}
                  onChange={(opt, config) => {
                    setCoverImposition(opt ? {
                      choice: {
                        id: opt.id,
                        label: opt.label,
                        items_per_sheet: opt.items_per_sheet,
                        base_sheets_needed: opt.sheets_needed,
                        sheets_needed: opt.sheets_needed,
                        usage: opt.usage,
                        pieces: opt.pieces,
                        sheet_cut: config?.sourceCut || null,
                      },
                      config,
                    } : null);
                  }}
                  defaultConfig={{
                    ...getImpositionDefaultConfig(budget),
                    doubleSided: true,
                  }}
                  itemWidth={coverW}
                  itemHeight={coverH}
                  quantity={qty}
                />
              )}
            </div>
          );
        })()}

        <div className="card editor-card">
          <h3 className="card-title">Observaciones</h3>
          <div className="form-group">
            <textarea
              className="form-input"
              rows={4}
              value={observations}
              onChange={(e) => setObservations(e.target.value)}
              placeholder="Notas internas, plazos, requerimientos especiales..."
            />
          </div>
        </div>

        <div className="editor-actions">
          {saveError && <div className="login-error">{saveError}</div>}
          <div className="editor-buttons">
            <button className="btn-secondary" onClick={() => navigate(`/presupuestos/${id}`)}>
              Cancelar
            </button>
            <button
              className="btn-primary-inline"
              onClick={handleSave}
              disabled={saving || Boolean(previewError)}
            >
              {saving ? 'Guardando...' : 'Guardar Cambios'}
            </button>
          </div>
        </div>
      </main>
    </div>
  );
};

export default BudgetEditor;
